import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "./supabase";
import { api, setCachedAccessToken, setSigningOut } from "./api";
import { makeSingleFlight } from "./singleFlight";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Refresh-restore fix: true only while the initial Supabase getSession() is STILL pending past
  // the 5s UX threshold (e.g. a network token refresh). Drives a "Restoring your session…" state.
  // It NEVER implies unauthenticated — the session is not nulled and loading stays blocked.
  const [slowRestore, setSlowRestore] = useState(false);
  // P0 provisioning-stability fix: surface a terminal provisioning error instead of leaving the
  // shell stuck on "Provisioning your profile…" forever when /auth/provision fails (stale/rotated
  // session 401, disabled account 403, missing profile, or transient/cold-start failures).
  const [provisionError, setProvisionError] = useState(null);
  // DR-2-A: shared single-flight wrapper so overlapping auth triggers (initial getSession +
  // onAuthStateChange) coalesce into ONE /auth/provision request. Created once; stable.
  const provisionRef = useRef(null);

  const provisionProfile = useCallback(async () => {
    if (!provisionRef.current) {
      provisionRef.current = makeSingleFlight(async () => {
        const MAX_ATTEMPTS = 3;
        const TIMEOUT_MS = 7000;
        setProvisionError(null);
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            const data = await Promise.race([
              api.post("/auth/provision").then((r) => r.data),
              new Promise((_, reject) =>
                setTimeout(() => reject(Object.assign(new Error("provision_timeout"), { __timeout: true })), TIMEOUT_MS)
              ),
            ]);
            setProfile(data);
            setProvisionError(null);
            return data;
          } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            const detailText = typeof detail === "string" ? detail : (detail?.message || detail?.code || "");
            // Terminal, non-retryable states -> show actionable message, stop looping.
            if (status === 403) {
              const disabled = /disabled/i.test(detailText) || detail?.code === "ACCOUNT_DISABLED";
              if (disabled) {
                setProvisionError({ kind: "disabled", message: "Account disabled. Please contact your Air12 admin." });
              } else if (detail && typeof detail === "object" && detail.message) {
                // RC-4: backend-authoritative block (operational-email collision / link-required).
                setProvisionError({ kind: "blocked", message: detail.message });
              } else {
                setProvisionError({ kind: "missing", message: "Your Air12 profile isn’t set up yet. Please contact your Air12 admin (invite incomplete)." });
              }
              return null;
            }
            if (status === 401) {
              setProvisionError({ kind: "session", message: "Your session has expired or this account no longer exists. Please sign out and sign in again." });
              return null;
            }
            // Transient (timeout / network / 5xx): retry a bounded number of times, then give up.
            if (attempt >= MAX_ATTEMPTS) {
              console.error("provision failed", e?.displayMessage || e);
              setProvisionError({ kind: "error", message: "We couldn’t finish setting up your account. Please refresh or contact your Air12 admin." });
              return null;
            }
            await new Promise((r) => setTimeout(r, 800 * attempt));
          }
        }
        return null;
      });
    }
    return provisionRef.current();
  }, []);

  useEffect(() => {
    let mounted = true;
    // Phase 2F.10 / Refresh-restore fix: the 5s startup timer is a UX THRESHOLD ONLY. When
    // getSession() is still pending after 5s (e.g. Supabase is doing a network token refresh on an
    // expired-but-valid session), we surface a "Restoring your session…" state but MUST NOT declare
    // the user unauthenticated: the timer never nulls the session/token nor flips loading off.
    // supabase.auth.getSession() remains the sole authority on whether a session exists.
    const STARTUP_TIMEOUT_MS = 5000;
    let restoreSettled = false;
    const startupTimer = setTimeout(() => {
      if (!mounted || restoreSettled) return;
      console.warn(
        `[auth] Supabase session restore exceeded ${STARTUP_TIMEOUT_MS}ms; ` +
          "showing restoring state (user is NOT logged out; still awaiting Supabase)."
      );
      setSlowRestore(true);
    }, STARTUP_TIMEOUT_MS);

    supabase.auth.getSession().then(({ data }) => {
      // Definitive startup result — clear the UX timer and slow-restore flag, then apply the
      // resolved session. A late timer can no longer overwrite this (restoreSettled guard above).
      restoreSettled = true;
      if (!mounted) return;
      clearTimeout(startupTimer);
      setSlowRestore(false);
      // Phase 2F.12: populate API token cache BEFORE any backend call so the request
      // interceptor never has to call supabase.auth.getSession() on the happy path.
      setCachedAccessToken(data.session?.access_token || null);
      setSession(data.session || null);
      // Module 7.1 fix: flip loading off as soon as the SESSION is known. Provision runs in the
      // background; ProtectedShell renders on a valid session and RoleRouter waits for `profile`.
      setLoading(false);
      // OPTION A (dedupe startup /auth/provision): getSession() no longer provisions. Initial
      // profile provisioning is owned solely by onAuthStateChange's guaranteed INITIAL_SESSION
      // event (carrying this same restored session), so ordinary startup issues exactly ONE
      // /auth/provision instead of a timing-dependent duplicate.
    }).catch((e) => {
      // Phase 2F.8 defensive guard: a genuine getSession rejection resolves to unauthenticated and
      // never strands the shell on "Loading…". This is a REAL restore error, not an elapsed timer.
      restoreSettled = true;
      console.error("getSession failed", e);
      if (!mounted) return;
      clearTimeout(startupTimer);
      setSlowRestore(false);
      setCachedAccessToken(null);
      setSession(null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, sess) => {
      // Phase 2F.12: keep the API token cache in lock-step with Supabase session updates.
      setCachedAccessToken(sess?.access_token || null);
      setSession(sess || null);
      if (sess) {
        await provisionProfile();
      } else {
        setProfile(null);
        setProvisionError(null);
      }
    });
    return () => {
      mounted = false;
      clearTimeout(startupTimer);
      sub?.subscription?.unsubscribe?.();
    };
  }, [provisionProfile]);

  const signOut = async () => {
    // Logout-race fix: tear down local auth state FIRST so ProtectedShell unmounts protected content
    // immediately and the request interceptor stops attaching the (about-to-be-revoked) token, then
    // flag the transition so any in-flight 401 "Invalid token" is suppressed from toasts. Only then
    // revoke the Supabase session. The flag is always cleared in finally.
    setSigningOut(true);
    setCachedAccessToken(null);
    setSession(null);
    setProfile(null);
    setProvisionError(null);
    try {
      await supabase.auth.signOut();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <AuthCtx.Provider value={{ session, profile, loading, slowRestore, provisionError, signOut, refreshProfile: provisionProfile }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
